import Stack from "@mui/material/Stack";
import { useEffect } from "react";

import ExportDataButton from "../../../common/components/button/ExportDataButton";
import AllLocationsEventsFilterDropdownContainer from "../../../common/components/locations/recent-events/AllLocationsEventsFilterDropdownContainer";
import EventSelect from "../../../common/components/select/EventSelect/EventSelect";
// import CameraFilterDropdown from "../../../common/components/layout/components/CameraFilterDropdown";
import {
  EVENTS_LOCATIONS_DROPDOWN_STORAGE_KEY,
  setLocationToLocalStorage,
} from "../../../common/helpers/getLocationFromLocalStorage";
import { useAuthenticatedUser } from "../../../common/hooks/useAuthenticatedUser";
import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import { RoleEnum } from "../../../common/models/enums";
import { selectedLocationVariable } from "../../../common/variables/selectedLocation";
import {
  selectedModelVariable,
  useSelectedModel,
} from "../../../common/variables/selectedModel";
import {
  selectedDataHubLocationVariable,
  selectedDataHubCameraVariable,
  // useSelectedDataHubCamera,
} from "../../data-hub/components/dataHub.variable";
import useExportRecentEventsData from "../hooks/export/useExportRecentEventsData";
import Box from "@mui/material/Box";
import ReactDatePicker from "../../../common/components/datePicker/ReactDatePicker";

interface RecentActivitiesFiltersProps {
  showExportButton?: boolean;
  showLocationFilter?: boolean;
  showModelFilter?: boolean;
  showDeviceFilter?: boolean;
  showLocationTags?: boolean; // controls whether tags appear in the location dropdown
}

const RecentActivitiesFilters = ({
  showExportButton = true,
  showLocationFilter = true,
  showModelFilter = true,
  showDeviceFilter = false,
  showLocationTags = false,
}: RecentActivitiesFiltersProps) => {
  const { downloadRecentEventsData, loading } = useExportRecentEventsData();
  const { role } = useAuthenticatedUser();

  const selectedModel = useSelectedModel();
  const setSelectedModel = selectedModelVariable;

  const setSelectedLocation = selectedLocationVariable;

  // const cameraVariable = useSelectedDataHubCamera();
  const setCameraVariable = selectedDataHubCameraVariable;

  // Auto-set gas leak for VERIFIER users
  const isVerifier = role === RoleEnum.VERIFIER;
  const gasLeakOption: AutocompleteOptionType = {
    title: "Gas leak",
    value: "GASLEAK",
  };

  // Set gas leak option for VERIFIER users if not already set
  useEffect(() => {
    if (isVerifier && (!selectedModel || selectedModel.value !== "GASLEAK")) {
      setSelectedModel(gasLeakOption);
    }
  }, [isVerifier, selectedModel, setSelectedModel, gasLeakOption]);

  const handleModelChange = (modelOption: AutocompleteOptionType | null) => {
    setSelectedModel(modelOption);
  };

  const handleLocationChange = (
    locationValue: AutocompleteOptionType | null
  ) => {
    setSelectedLocation(locationValue);

    // Clear camera selection when location changes to avoid mismatched camera
    setCameraVariable(null);

    // Update the selected for legacy Data Hub pages
    selectedDataHubLocationVariable(locationValue);

    // Persist only when a concrete location is chosen; do not clear tag storage on null
    if (locationValue) {
      setLocationToLocalStorage(
        EVENTS_LOCATIONS_DROPDOWN_STORAGE_KEY,
        locationValue
      );
    }
  };

  const handleTagLocationsChange = (
    tagLocations: AutocompleteOptionType[] | null
  ) => {
    const nextLocation =
      tagLocations && tagLocations.length ? tagLocations[0] : null;

    setSelectedLocation(nextLocation);

    setCameraVariable(null);

    selectedDataHubLocationVariable(nextLocation);
  };

  const handleDownloadClick = async () => {
    try {
      await downloadRecentEventsData();
    } catch (error) {
      console.error("Error downloading data:", error);
    }
  };

  // const handleCameraChange = (camera: AutocompleteOptionType | null) => {
  //   setCameraVariable(camera);
  // };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Box sx={{ minWidth: "180px" }}>
          <ReactDatePicker placeholderText="All time" />
        </Box>
        {showLocationFilter && (
          <Box sx={{ minWidth: "180px" }}>
            <AllLocationsEventsFilterDropdownContainer
              setLocation={handleLocationChange}
              setTagLocations={handleTagLocationsChange}
              ignoreDefaultValue
              disableFilteringByTags={!showLocationTags}
            />
          </Box>
        )}
        {/* {showDeviceFilter && (
          <Box sx={{ minWidth: "180px" }}>
            <CameraFilterDropdown
              selectedValue={cameraVariable ?? null}
              setSelectedValue={handleCameraChange}
            />
          </Box>
        )} */}
        {showModelFilter && (
          <Box sx={{ minWidth: "180px" }}>
            <EventSelect value={selectedModel} onChange={handleModelChange} />
          </Box>
        )}
      </Stack>
      {showExportButton && (
        <ExportDataButton
          sx={{
            color: theme => theme.palette.text.primary,
            marginLeft: "26px",
            "&.Mui-disabled": {
              color: "inherit",
              backgroundColor: "transparent",
            },
          }}
          onClick={handleDownloadClick}
          loading={loading}
        >
          Export Data
        </ExportDataButton>
      )}
    </Stack>
  );
};

export default RecentActivitiesFilters;
